import { IS_APP } from "@/__main__/constants.mjs";
import {
  ACTIVE_GAMES,
  GAME_SHORT_NAMES,
  GAMES_COMING_SOON,
  HUB_SYMBOL_HOME,
} from "@/app/constants.mjs";
import { deriveGamesPropMap, getGameDefinition } from "@/app/games.mjs";
import {
  syncAppVersion,
  syncLatestMacRelease,
  syncLatestWindowsRelease,
} from "@/hub-settings/actions.mjs";
import { fetchDepartments } from "@/marketing/actions.mjs";
import homeRefs from "@/marketing/HomeGamePage.refs.jsx";
import gameRoutes from "@/routes/game.mjs";
import type { BaseRoute } from "@/routes/routes.mjs";
import lazyFn from "@/util/lazy-fn.mjs";
import pathRegExp from "@/util/path-regexp.mjs";

const fetchDatas = {
  ...deriveGamesPropMap(
    "meta",
    (m) => (m as PartialRecord<string, unknown>)?.homeData,
  ),
};

export const homepageRoutes: BaseRoute[] = [
  {
    path: "/",
    component: "marketing/Home.tsx",
    hubSymbol: HUB_SYMBOL_HOME,
  },
];

for (const gameSymbol of ACTIVE_GAMES) {
  gameRoutes[gameSymbol].push({
    path: `/${GAME_SHORT_NAMES[gameSymbol]}`,
    component: "marketing/Home.tsx",
    hubSymbol: HUB_SYMBOL_HOME,
    async fetchData(...args) {
      const gameDef = getGameDefinition(gameSymbol);

      // Reset
      const g = gameDef.meta?.guides ?? [];
      homeRefs.hasGuides = Boolean(g.length);
      homeRefs.guides = [];
      homeRefs.Leaderboard = null;

      const dataPromise = fetchDatas[gameSymbol]
        ? lazyFn(fetchDatas[gameSymbol])(...args)
        : Promise.resolve();

      await Promise.all([
        dataPromise,
        (async () => {
          const guides = await Promise.all(
            g.map(async (fn) => {
              // @ts-ignore
              const Component = (await fn()).default;
              return Component;
            }),
          );
          homeRefs.guides = guides;
        })(),
        (async () => {
          const fn = gameDef.meta?.leaderboard;
          if (!fn) return;
          // @ts-ignore
          const Component = (await fn()).default;
          homeRefs.Leaderboard = Component;
        })(),
      ]);
    },
  });
}

export const appRoutes: BaseRoute[] = [];

if (GAMES_COMING_SOON.length > 0) {
  appRoutes.push(
    ...GAMES_COMING_SOON.map((gameSymbol) => ({
      path: `/${GAME_SHORT_NAMES[gameSymbol]}`,
      component: "marketing/GameComingSoon.tsx",
    })),
  );
}

export const standaloneRoutes: BaseRoute[] = !IS_APP
  ? [
      {
        path: "/welcome",
        redirect: "/welcome/lol",
      },
      {
        path: pathRegExp(
          "/welcome/:game(apex|cs2|eft|fortnite|lol|tft|valorant|marvel-rivals)",
        ),
        component: "marketing/WelcomeRedesign.tsx",
      },
      {
        path: "/about-us",
        component: "marketing/AboutUs.tsx",
      },
      {
        path: "/partnerships",
        component: "marketing/Partnerships.tsx",
      },
      {
        path: "/brand-assets",
        component: "marketing/BrandAssets.tsx",
      },
      {
        path: "/careers",
        component: "marketing/Careers.tsx",
        async fetchData() {
          await fetchDepartments();
        },
      },
      {
        path: /^\/download(\/mac|\/win)?/,
        component: "marketing/Download.tsx",
        async fetchData() {
          await Promise.all([
            syncAppVersion(),
            syncLatestWindowsRelease(),
            syncLatestMacRelease(),
          ]);
        },
      },
      {
        path: "/uninstall-survey",
        component: "marketing/UninstallSurvey.jsx",
      },
    ]
  : [];
